import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useInvalidateOperationsDashboard,
  useOperationsDashboard,
  type LifecycleStageId,
  type LifecycleTile,
  type ProjectRisk,
  type ProjectSummary,
  type RiskKind,
} from "../api";

import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { SyncActivityBadge } from "../components/SyncActivityBadge";
import {
  allOrgs,
  projectMatchesOrgs,
  projectMatchesSearch,
} from "../utils/projectOrgs";
import { useDocumentTitle } from "../utils/useDocumentTitle";

import styles from "./OperationsRoute.module.css";

// Operations is the activity dashboard. Top-down:
//   1. Lifecycle filter tiles - click a stage to scope the project
//      list below. "All" returns the full list. Counts + kWp totals
//      stay visible on every tile.
//   2. Project list - one row per project (filtered by selected
//      stage). Each row carries a mini-summary: name + address,
//      the one-line next step, and any risk chips (a failed file).
//   3. Drafts list - projects not yet submitted.

const STAGE_TONE: Record<LifecycleStageId, string> = {
  predevelopment: styles.stagePredev,
  construction: styles.stageConstruction,
  operational: styles.stageOperational,
  decommissioned: styles.stageDecommissioned,
};

const RISK_LABEL: Record<RiskKind, string> = {
  failed_extraction: "Failed file",
};

const RISK_TONE_CLASS: Record<RiskKind, string> = {
  failed_extraction: styles.riskFailed,
};

type StageFilter = LifecycleStageId | "all";
type RiskFilter = RiskKind | "all";

// Risk-kind filter chips, in the order they render above the project
// list. ``next_step`` (BE concept) is not a RiskKind so it's not
// represented here; the user only wants chips for the kinds that
// actually appear as red/amber/yellow tags.
const RISK_FILTER_ORDER: RiskKind[] = ["failed_extraction"];

export const OperationsRoute = () => {
  useDocumentTitle("Operations");
  const dashQuery = useOperationsDashboard();
  const invalidate = useInvalidateOperationsDashboard();
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  // Default to pre-development - that's the stage that needs the
  // operator most. Click another tile to switch.
  const [stage, setStage] = useState<StageFilter>("predevelopment");
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");
  // Org + free-text filters scope only the project list below (the lifecycle
  // tile counts + risk-chip counts stay portfolio-wide). Mirrors the projects
  // page filter bar.
  const [search, setSearch] = useState("");
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);

  useEffect(() => {
    if (!dashQuery.isFetching && dashQuery.isSuccess) {
      setLastRefreshedAt(new Date());
    }
  }, [dashQuery.isFetching, dashQuery.isSuccess]);

  const orgOptions = useMemo(
    () => allOrgs(dashQuery.data?.projects ?? []),
    [dashQuery.data],
  );

  // Drop selected orgs that no longer exist once the list refetches, so a
  // stale filter can't silently hide every project.
  useEffect(() => {
    const valid = new Set(orgOptions.map((o) => o.toLowerCase()));
    setSelectedOrgs((prev) => {
      const next = prev.filter((o) => valid.has(o.toLowerCase()));
      return next.length === prev.length ? prev : next;
    });
  }, [orgOptions]);

  // If the default filter ends up empty but other stages have
  // projects, drop to "all" so the operator sees something.
  useEffect(() => {
    if (!dashQuery.data || stage !== "predevelopment") return;
    const predevTile = dashQuery.data.lifecycle.find((t) => t.id === "predevelopment");
    const total = dashQuery.data.projects.length;
    if (predevTile && predevTile.count === 0 && total > 0) {
      setStage("all");
    }
  }, [dashQuery.data, stage]);

  // Count how many projects carry at least one risk of each kind,
  // scoped to the currently-selected lifecycle stage (the chip count
  // matches what the user actually sees in the list below).
  const riskCounts = useMemo(() => {
    const counts: Record<RiskKind, number> = {
      failed_extraction: 0,
    };
    if (!dashQuery.data) return counts;
    const stageScoped =
      stage === "all"
        ? dashQuery.data.projects
        : dashQuery.data.projects.filter(
            (p) =>
              p.lifecycle_stage === stage ||
              (stage === "predevelopment" && p.lifecycle_stage === "unknown"),
          );
    for (const p of stageScoped) {
      const seen = new Set<RiskKind>();
      for (const r of p.risks) {
        if (!seen.has(r.kind)) {
          counts[r.kind] += 1;
          seen.add(r.kind);
        }
      }
    }
    return counts;
  }, [dashQuery.data, stage]);

  // Projects in the selected lifecycle stage + risk filter, before the org /
  // search filters narrow it. This is the denominator for the "X of Y" count.
  const stageRiskScoped = useMemo(() => {
    if (!dashQuery.data) return [];
    const stageScoped =
      stage === "all"
        ? dashQuery.data.projects
        : dashQuery.data.projects.filter(
            (p) =>
              p.lifecycle_stage === stage ||
              (stage === "predevelopment" && p.lifecycle_stage === "unknown"),
          );
    return riskFilter === "all"
      ? stageScoped
      : stageScoped.filter((p) => p.risks.some((r) => r.kind === riskFilter));
  }, [dashQuery.data, stage, riskFilter]);

  const orgSearchActive = selectedOrgs.length > 0 || search.trim() !== "";

  const filteredProjects = useMemo(() => {
    const orgKeys = new Set(selectedOrgs.map((o) => o.toLowerCase()));
    const filtered = stageRiskScoped.filter(
      (p) =>
        projectMatchesOrgs(p, orgKeys) &&
        projectMatchesSearch(p.project_name, p.address, search),
    );
    // Sort alphabetically by name (case-insensitive, locale-aware).
    return [...filtered].sort((a, b) =>
      a.project_name.localeCompare(b.project_name, "en-GB", {
        sensitivity: "base",
      }),
    );
  }, [stageRiskScoped, search, selectedOrgs]);

  if (dashQuery.isLoading && !dashQuery.data) {
    return (
      <section className={styles.section}>
        <Head onRefresh={invalidate} fetching lastRefreshedAt={null} />
        <div className={styles.empty}>Loading portfolio…</div>
      </section>
    );
  }
  if (dashQuery.error || !dashQuery.data) {
    return (
      <section className={styles.section}>
        <Head onRefresh={invalidate} fetching={dashQuery.isFetching} lastRefreshedAt={null} />
        <div className={styles.err}>
          Couldn't load Operations: {(dashQuery.error as Error)?.message ?? "no data"}
        </div>
      </section>
    );
  }

  const data = dashQuery.data;
  const noPortfolio = data.projects.length === 0 && data.drafts.length === 0;

  return (
    <section className={styles.section}>
      <Head
        onRefresh={invalidate}
        fetching={dashQuery.isFetching}
        lastRefreshedAt={lastRefreshedAt}
      />

      {noPortfolio ? (
        <div className={styles.empty}>
          <strong>No projects yet.</strong>
          <p>Use the New project button at the top of the sidebar to start one.</p>
        </div>
      ) : (
        <>
          <LifecycleFilter
            tiles={data.lifecycle}
            stage={stage}
            onSelect={setStage}
            totalProjects={data.projects.length}
          />
          <div className={styles.filterRow}>
            <div className={styles.opsFilters}>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Search projects by name or address…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <MultiSelectFilter
                options={orgOptions}
                selected={selectedOrgs}
                onChange={setSelectedOrgs}
              />
              {orgSearchActive && (
                <>
                  <span className={styles.resultCount}>
                    {filteredProjects.length} of {stageRiskScoped.length}
                  </span>
                  <button
                    type="button"
                    className={styles.clearFilters}
                    onClick={() => {
                      setSearch("");
                      setSelectedOrgs([]);
                    }}
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
            <RiskFilterChips
              riskFilter={riskFilter}
              onSelect={setRiskFilter}
              counts={riskCounts}
            />
          </div>
          <ProjectList
            projects={filteredProjects}
            stage={stage}
            riskFilter={riskFilter}
            orgSearchActive={orgSearchActive}
          />
          {data.drafts.length > 0 && <DraftsList drafts={data.drafts} />}
        </>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------- //
// Head
// ---------------------------------------------------------------------- //
const Head = ({
  onRefresh,
  fetching,
  lastRefreshedAt,
}: {
  onRefresh: () => void;
  fetching: boolean;
  lastRefreshedAt: Date | null;
}) => (
  <div className={styles.head}>
    <div>
      <h2 className={styles.h2}>Operations</h2>
      <p className={styles.lede}>
        Click a lifecycle stage to filter the project list. Each project shows
        its next step + any open risks.
      </p>
    </div>
    <div className={styles.refreshGroup}>
      {lastRefreshedAt && !fetching && (
        <span className={styles.lastRefreshed}>
          Last refreshed {lastRefreshedAt.toLocaleTimeString()}
        </span>
      )}
      <button className={styles.refreshBtn} onClick={onRefresh} disabled={fetching}>
        {fetching ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------- //
// Lifecycle filter (top of page, replaces the KPI strip)
// ---------------------------------------------------------------------- //
const LifecycleFilter = ({
  tiles,
  stage,
  onSelect,
  totalProjects,
}: {
  tiles: LifecycleTile[];
  stage: StageFilter;
  onSelect: (s: StageFilter) => void;
  totalProjects: number;
}) => {
  const totalKwp = tiles.reduce((acc, t) => acc + t.kwp_total, 0);
  return (
    <div className={styles.lifecycle}>
      <LifecycleButton
        active={stage === "all"}
        onClick={() => onSelect("all")}
        count={totalProjects}
        kwpTotal={totalKwp}
        label="All projects"
        toneClass={styles.stageAll}
      />
      {tiles.map((t) => (
        <LifecycleButton
          key={t.id}
          active={stage === t.id}
          onClick={() => onSelect(t.id)}
          count={t.count}
          kwpTotal={t.kwp_total}
          label={t.label}
          toneClass={STAGE_TONE[t.id]}
        />
      ))}
    </div>
  );
};

const LifecycleButton = ({
  active,
  onClick,
  count,
  kwpTotal,
  label,
  toneClass,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  kwpTotal: number;
  label: string;
  toneClass: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`${styles.lifecycleStage} ${toneClass} ${
      active ? styles.lifecycleStageActive : ""
    } ${count === 0 ? styles.lifecycleStagePlanned : ""}`}
  >
    <div className={styles.lifecycleCount}>{count}</div>
    <div className={styles.lifecycleLabel}>{label}</div>
    <div className={styles.lifecycleMw}>
      {kwpTotal > 0
        ? `${Math.round(kwpTotal).toLocaleString("en-GB")} kWp`
        : "-"}
    </div>
  </button>
);

// ---------------------------------------------------------------------- //
// Risk filter chips - sits between the lifecycle filter and the project
// list. Click a chip to scope the list to projects that carry at least
// one risk of that kind. ``All`` returns the full list. Counts are
// scoped to the currently-selected lifecycle stage.
// ---------------------------------------------------------------------- //
const RiskFilterChips = ({
  riskFilter,
  onSelect,
  counts,
}: {
  riskFilter: RiskFilter;
  onSelect: (k: RiskFilter) => void;
  counts: Record<RiskKind, number>;
}) => {
  const totalWithRisks = RISK_FILTER_ORDER.reduce(
    (acc, k) => acc + counts[k],
    0,
  );
  // Only render chips for risk kinds that actually appear in the current
  // stage (count > 0). The active chip stays visible even if its count
  // drops to 0 so the operator can always unselect it.
  const visibleKinds = RISK_FILTER_ORDER.filter(
    (k) => counts[k] > 0 || riskFilter === k,
  );
  return (
    <div className={styles.riskFilters}>
      <button
        type="button"
        className={`${styles.riskFilterChip} ${
          riskFilter === "all" ? styles.riskFilterChipActive : ""
        }`}
        onClick={() => onSelect("all")}
      >
        All
      </button>
      {visibleKinds.map((k) => (
        <button
          key={k}
          type="button"
          className={`${styles.riskFilterChip} ${RISK_TONE_CLASS[k]} ${
            riskFilter === k ? styles.riskFilterChipActive : ""
          }`}
          onClick={() => onSelect(riskFilter === k ? "all" : k)}
        >
          {RISK_LABEL[k]}
          <span className={styles.riskFilterChipCount}>{counts[k]}</span>
        </button>
      ))}
      {totalWithRisks === 0 && (
        <span className={styles.riskFilterEmpty}>No open risks in this stage.</span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------- //
// Project list
// ---------------------------------------------------------------------- //
const ProjectList = ({
  projects,
  stage,
  riskFilter,
  orgSearchActive,
}: {
  projects: ProjectSummary[];
  stage: StageFilter;
  riskFilter: RiskFilter;
  orgSearchActive: boolean;
}) => {
  if (projects.length === 0) {
    return (
      <p className={styles.bandEmpty}>
        {orgSearchActive
          ? "No projects match the current org / search filters."
          : riskFilter !== "all"
            ? `No projects in this stage with ${RISK_LABEL[riskFilter as RiskKind].toLowerCase()}.`
            : stage === "all"
              ? "No projects in the portfolio yet."
              : "No projects in this stage."}
      </p>
    );
  }
  return (
    <div className={styles.projectList}>
      {projects.map((p) => (
        <ProjectCard key={p.project_id} project={p} />
      ))}
    </div>
  );
};

const ProjectCard = ({ project }: { project: ProjectSummary }) => (
  <article className={styles.projectCard}>
    <div className={styles.projectHead}>
      <div className={styles.projectHeadLeft}>
        <Link
          to={`/projects/${encodeURIComponent(project.project_id)}`}
          className={styles.projectName}
        >
          {project.project_name}
        </Link>
        {project.address && (
          <div className={styles.projectAddress}>{project.address}</div>
        )}
      </div>
      <div className={styles.projectHeadRight}>
        <SyncActivityBadge
          projectId={project.project_id}
          recent={project.drive_sync_recent}
          compact
        />
        <span
          className={`${styles.stagePill} ${STAGE_TONE[project.lifecycle_stage as LifecycleStageId] ?? ""}`}
        >
          {project.lifecycle_stage}
        </span>
      </div>
    </div>

    {project.next_step && (
      <div className={styles.nextStep}>
        <span className={styles.nextStepLabel}>Next</span>
        <span className={styles.nextStepBody}>{project.next_step}</span>
      </div>
    )}

    {project.risks.length > 0 && (
      <ul className={styles.riskList}>
        {project.risks.map((r, i) => (
          <RiskChip
            key={`${r.kind}:${i}`}
            risk={r}
            projectId={project.project_id}
          />
        ))}
      </ul>
    )}

    {project.risks.length === 0 && !project.next_step && (
      <div className={styles.projectIdle}>Idle - no open risks.</div>
    )}
  </article>
);

const RiskChip = ({
  risk,
  projectId,
}: {
  risk: ProjectRisk;
  projectId: string;
}) => {
  const chipClass = `${styles.riskChip} ${RISK_TONE_CLASS[risk.kind]}`;
  const label = RISK_LABEL[risk.kind];
  // When the BE title duplicates the kind label, drop the duplicate row
  // and inline the age pill next to the kind tag instead.
  const titleDuplicatesLabel =
    risk.title.trim().toLowerCase() === label.toLowerCase();
  const ageEl =
    risk.age_days != null ? (
      <span className={styles.riskAge}>{formatAge(risk.age_days)}</span>
    ) : null;
  const body = (
    <>
      <span className={styles.riskKindRow}>
        <span className={styles.riskKind}>{label}</span>
        {titleDuplicatesLabel && ageEl}
      </span>
      {!titleDuplicatesLabel && (
        <>
          <span className={styles.riskTitle}>{risk.title}</span>
          {ageEl}
        </>
      )}
      {risk.detail && <div className={styles.riskDetail}>{risk.detail}</div>}
    </>
  );
  if (risk.kind === "failed_extraction") {
    return (
      <li>
        <Link
          to={`/projects/${encodeURIComponent(projectId)}/files`}
          className={`${chipClass} ${styles.riskChipLink}`}
          title="Open the Files tab to see the error and re-upload"
        >
          {body}
        </Link>
      </li>
    );
  }
  return <li className={chipClass}>{body}</li>;
};

// ---------------------------------------------------------------------- //
// Drafts
// ---------------------------------------------------------------------- //
const DraftsList = ({
  drafts,
}: {
  drafts: { project_id: string; project_name: string }[];
}) => (
  <div className={styles.drafts}>
    <div className={styles.draftsHead}>
      {drafts.length} draft project{drafts.length === 1 ? "" : "s"} - not yet
      submitted
    </div>
    <ul className={styles.draftsList}>
      {drafts.map((p) => (
        <li key={p.project_id}>
          <Link
            to={`/projects/${encodeURIComponent(p.project_id)}`}
            className={styles.draftLink}
          >
            {p.project_name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// ---------------------------------------------------------------------- //
// Helpers
// ---------------------------------------------------------------------- //
function formatAge(days: number): string {
  if (days <= 0) return "today";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}
